import { FC } from "react";

import { useAssessedCountData, useFirstAssessableResponseId } from "../../utils/assessment";
import { useQueryParamsManager } from "../../utils/routing";
import { getNavigationValues } from "../../utils/urls";

import {
  ACCESS_ID_QUERY_PARAM,
  ASSESSMENT_VIEW_QUERY_PARAM,
  SUBMISSION_ID_QUERY_PARAM,
} from "components/constants";
import { BaseFooter } from "components/footer/BaseFooter";
import { LAST_ASSESSMENT_PANEL_ACCESS_ID } from "components/materials/page/assessment/helpers";
import { useResponsesForPage } from "components/materials/page/responses";
import { PageType } from "components/server-types";
import { t } from "i18n/i18n";
import { IconText } from "mds/components/IconText";
import { PillButton, PillButtonProps } from "mds/components/PillButton";
import { Tooltip } from "mds/components/Tooltip";
import { useIsSmOrLarger } from "mds/hooks/use-responsive";
import { ChevronLeftIcon, ChevronRightIcon } from "mds/icons";
import { useAppSelector } from "store/index";
import {
  selectCurrentCourseUserId,
  selectDefaultSubmissionForUser,
  selectFullSubmissionById,
  selectShouldShowModalOnExitAssessmentMode,
} from "store/selectors";

export type AssessmentProgressBarProps = {
  selectedPage: PageType | undefined;
};

export const AssessmentProgressBar: FC<AssessmentProgressBarProps> = ({ selectedPage }) => {
  const courseUserId = useAppSelector(selectCurrentCourseUserId);
  const { getQueryParam, setQueryParams } = useQueryParamsManager();
  const { responses } = useResponsesForPage(selectedPage);

  const selectedAccessId = getQueryParam(ACCESS_ID_QUERY_PARAM);
  const isLastPanel = selectedAccessId === LAST_ASSESSMENT_PANEL_ACCESS_ID;

  const submissionId = getQueryParam(SUBMISSION_ID_QUERY_PARAM);
  const submission = useAppSelector((s) => selectFullSubmissionById(s, submissionId));

  const isNextDisabled = !courseUserId || isLastPanel;

  const firstId = useFirstAssessableResponseId(selectedPage, responses);
  const isNavigationDisabled = useAppSelector(selectShouldShowModalOnExitAssessmentMode);

  const panels = responses.map((response) => response.id).concat([LAST_ASSESSMENT_PANEL_ACCESS_ID]);

  const accessParamId = getQueryParam(ACCESS_ID_QUERY_PARAM);
  const currentId = accessParamId || firstId;

  const { nextId, prevId, nextURL, prevURL } = getNavigationValues(panels, {
    queryParam: ACCESS_ID_QUERY_PARAM,
    currentId,
  });

  const submissionForNextUser = useAppSelector((s) =>
    selectDefaultSubmissionForUser(s, selectedPage?.id ?? "", nextId, true),
  );

  const submissionForPrevUser = useAppSelector((s) =>
    selectDefaultSubmissionForUser(s, selectedPage?.id ?? "", prevId, true),
  );

  const assessedCountData = useAssessedCountData(selectedPage);
  const { count, total } = assessedCountData;

  const isMobile = !useIsSmOrLarger();

  // Early return after all hooks
  if (!selectedPage) return null;

  if (nextURL && submissionForNextUser) {
    nextURL.searchParams.set(SUBMISSION_ID_QUERY_PARAM, submissionForNextUser.id);
  }

  if (prevURL && submissionForPrevUser) {
    prevURL.searchParams.set(SUBMISSION_ID_QUERY_PARAM, submissionForPrevUser.id);
  }

  nextURL?.searchParams?.set(ASSESSMENT_VIEW_QUERY_PARAM, "1");
  prevURL?.searchParams?.set(ASSESSMENT_VIEW_QUERY_PARAM, "1");

  const percentage = total === 0 ? 0 : Math.ceil((count / total) * 100);
  const safePercentage = Math.min(100, Math.max(0, percentage));

  const onBack = () => {
    setQueryParams({
      [ACCESS_ID_QUERY_PARAM]: prevId || null,
      [SUBMISSION_ID_QUERY_PARAM]: submissionForPrevUser?.id || null,
    });
  };

  const onNext = () => {
    setQueryParams({
      [ACCESS_ID_QUERY_PARAM]: nextId || null,
      [SUBMISSION_ID_QUERY_PARAM]: submissionForNextUser?.id || null,
    });
  };

  const nextButtonAttrs: PillButtonProps = submission
    ? { form: "assessment-grading-form", type: "submit" }
    : {};

  return (
    <BaseFooter>
      <div className="flex w-full items-center justify-center gap-3 text-sm font-medium text-black-tint-40">
        <PillButton
          disabled={!prevId || isNavigationDisabled}
          eventName="Assessment - Previous"
          kind="gray"
          size="m"
          onClick={onBack}
        >
          <IconText iconStart={<ChevronLeftIcon />} text={t("common.previous")} />
        </PillButton>

        <div className="flex w-[60px] items-center justify-end whitespace-nowrap">
          {t("common.index_of_total_counter", { index: count, total })}
        </div>

        {!isMobile && (
          <>
            <div className="flex w-full max-w-[1000px] flex-grow">
              <div className="h-3 w-full rounded-full bg-green-tint-70">
                <div
                  className="duration-400 h-3 rounded-full bg-green-shade-20 transition-[width] ease-in-out"
                  style={{ width: `${safePercentage}%` }}
                />
              </div>
            </div>

            <div className="flex w-[40px] items-center justify-start">
              {t("common.percentage", { number: safePercentage })}
            </div>
          </>
        )}

        <Tooltip
          text={!courseUserId ? t("assessment_card.cannot_assess_without_enrollment") : undefined}
        >
          <PillButton
            disabled={isNextDisabled}
            eventName="Assessment - Next"
            kind="green"
            size="m"
            onClick={!submission ? onNext : undefined}
            {...nextButtonAttrs}
          >
            <IconText iconEnd={<ChevronRightIcon />} text={t("common.next")} />
          </PillButton>
        </Tooltip>
      </div>
    </BaseFooter>
  );
};
